import React, { useState, useEffect, FC } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Icon } from "../../components/Component";
import { Button } from "reactstrap";
import { Modal } from "antd";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}
interface BuyerDetails {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  gstin: string;
  pan: string;
  cin: string;
}

interface Lines {
  sno: number;
  itemDescription: string;
  hsnSac: string;
  qty?: number;
  rate: number;
  cgstPercentage: number;
  cgstAmount: number;
  sgstPercentage: number;
  sgstAmount: number;
  taxableAmount: number;
  uom: string;
}

interface Totals {
  taxableAmount: string;
  cgst: string;
  sgst: string;
  total: string;
}

interface Data {
  quoteNumber: string;
  quoteDate: string;
  terms: string;
  expectedShipmentDate: string;
  placeofSupply: string;
  creditDays: number;
  deliveryAddressString: string;
  lines: Lines[];
  totals: Totals;
}

interface FooterData {
  orgName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  pincode: string;
  state: string;
  email: string;
  phoneNumber: string;
  cin: string;
  website: string;
}

interface Extras {
  termsandconditions?: string;
  disclaimer?: string;
}

const PdfGenerator: FC<any> = ({
  buyerDetails,
  supplierDetails,
  Details,
  buyerOrgLogo,
  hasIGST,
  prApprovals,
}: any) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };
  const [htmlContent, setHtmlContent] = useState(`
    <h1>Welcome to ProcureX</h1>
    <p>
      ProcureX is an innovative <strong>AI-based procurement platform</strong> designed to simplify the work scheduling and site inventory process.
    </p>
    <h2>Why Choose Us?</h2>
    <ul>
      <li>Efficient work scheduling</li>
      <li>Real-time inventory management</li>
      <li>AI-based forecasting and planning</li>
    </ul>
    <p>Thank you for choosing ProcureX, your partner in procurement!</p>
  `);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);
  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(`${url}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            console.log(
              "Image successfully fetched and converted to Base64:",
              reader.result
            ); // Debugging line
            resolve(reader.result as string);
          } else {
            reject(new Error("Failed to read the image data"));
          }
        };
        reader.onerror = () =>
          reject(new Error("Error reading the image data"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching the image:", error);
      throw new Error("Failed to fetch the image.");
    }
  };

  const drawBorderedBox = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Define the margins for the box (e.g., 10px margins)
    const margin = 5;
    const boxX = margin;
    const boxY = 20; // Start after the header
    const boxWidth = pageWidth - 2 * margin;
    const boxHeight = pageHeight - 41; // Leave space for the footer at the bottom

    // Draw the border box
    doc.setDrawColor(157, 157, 157);
    doc.setLineWidth(0.2);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
  };

  const generatePDF = async (download?: boolean) => {
    const doc = new jsPDF();
    const extras = {
      termsandconditions: `
        <h2 style="text-align:center;">Terms and Conditions</h2>
        <p>All goods are subject to the following terms and conditions:</p>
        <ol>
          <li>The supplier must deliver the items as per the agreed schedule.</li>
          <li>All goods must comply with the agreed specifications and quality standards.</li>
          <li>Late deliveries will incur a penalty of 5% per day of delay.</li>
        </ol>
      `,
      disclaimer: `
        <h2 style="text-align:center;">Disclaimer</h2>
        <p>The information contained in this document is confidential and intended solely for the recipient. Any unauthorized use, disclosure, or distribution is prohibited.</p>
        <p>The supplier assumes full responsibility for any errors or omissions contained herein.</p>
      `,
    };

    //const leftLogo = await fetchImageAsBase64('http://localhost:3000/static/media/logo-dark2x.8758ccf4e7a33281ce73.png');

    const addImageWithMaxHeight = async (
      doc: any,
      imageData: string,
      x: number,
      y: number,
      maxHeight: number
    ) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = imageData;

        img.onload = function () {
          const aspectRatio = img.width / img.height;

          // Ensure that the image height is capped at maxHeight, adjusting width accordingly
          const height = Math.min(img.height, maxHeight); // Restrict height to maxHeight
          const width = height * aspectRatio; // Adjust width based on aspect ratio

          doc.addImage(img.src, "PNG", x, y, width, height); // Add image to the PDF
          resolve();
        };

        img.onerror = function (error) {
          console.error("Image failed to load", error);
          reject(new Error("Image load failed"));
        };
      });
    };

    const addHeader = async (doc: jsPDF, leftLogo: string | null) => {
      if (leftLogo) {
        // If the image fetch was successful, add the image
        await addImageWithMaxHeight(doc, leftLogo, 10, 5, 10); // Await to ensure it finishes before continuing
      } else {
        // If the image fetch failed, print fallback text
        doc.setFontSize(20);
        doc.text("Procurex", 10, 20); // Fallback text in case of image fetch failure
      }
    };

    const breakLongText = (text: any, maxLineLength = 30) => {
      if (!text) return " ";
      return text.match(new RegExp(`.{1,${maxLineLength}}`, "g")).join("\n");
    };

    const addFooter = async (doc: jsPDF) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(7);

      const line1 = `${buyerDetails.name}`;
      const line2 = `${buyerDetails.addressLine1} ${buyerDetails.addressLine2}, ${buyerDetails?.city} ${buyerDetails.pincode}, ${buyerDetails.state}, ${buyerDetails.country}`;
      const line3 = `CIN: ${buyerDetails.cin} `;
      const line4 = `GSTIN: ${buyerDetails.gstin} | PAN: ${buyerDetails.pan}`;

      const lastLineY = pageHeight - 7;
      const lineSpacing = 3;
      const startY = lastLineY - 3 * lineSpacing;

      doc.text(line1, pageWidth / 2, startY, { align: "center" });
      doc.text(line2, pageWidth / 2, startY + lineSpacing, { align: "center" });
      doc.text(line3, pageWidth / 2, startY + 2 * lineSpacing, {
        align: "center",
      });
      doc.text(line4, pageWidth / 2, startY + 3 * lineSpacing, {
        align: "center",
      });

      const pageTextY = startY - 8;
      doc.text(" ", 10, pageTextY);
    };
    const addGeneralDetailsSection = async (
      doc: jsPDF,
      buyerDetails: BuyerDetails,
      Details: Data,
      supplierDetails: any
    ) => {
      const sectionY = 16; // Starting Y position for the General Details Section
      const pageWidth = doc.internal.pageSize.getWidth();

      // Set font for heading and add text for General Details Section
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold"); // Bold for the heading
      doc.setTextColor(0, 0, 0); // Black text color

      let currentY = sectionY + 10; // Keep track of the current Y position

      // Add buyer details
      if (buyerDetails) {
        const lineHeight = 4;
        doc.text(buyerDetails.name || "", 10, currentY);
        currentY += 5; // Move Y position for the next line

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");

        const address1 = breakLongText(buyerDetails.addressLine1 || "");
        doc.text(address1, 10, currentY);
        currentY += address1.split("\n").length * lineHeight;

        const address2 = breakLongText(buyerDetails.addressLine2 || "");
        doc.text(address2, 10, currentY);
        currentY += address2.split("\n").length * lineHeight;

        doc.text(
          `${buyerDetails.city || ""}, ${buyerDetails.state || ""}, ${
            buyerDetails.pincode || ""
          },${buyerDetails.country || ""}`,
          10,
          currentY
        );
        currentY += 5;
        const gstin = breakLongText(`GSTIN: ${buyerDetails.gstin || ""}`);
        doc.text(gstin, 10, currentY);
        currentY += gstin.split("\n").length * lineHeight;

        const pan = breakLongText(`PAN: ${buyerDetails.pan || ""}`);
        doc.text(pan, 10, currentY);
        currentY += pan.split("\n").length * lineHeight;

        const cin = breakLongText(`CIN: ${buyerDetails.cin || ""}`);
        doc.text(cin, 10, currentY);
        currentY += cin.split("\n").length * lineHeight;

        currentY += 5; // Add extra space after buyer details
      }

      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      const purchaseOrderText = "PURCHASE REQUEST";

      // Calculate X position for the text to be right-aligned
      const textWidth = doc.getTextWidth(purchaseOrderText); // Get the width of the text
      const rightX = pageWidth - textWidth - 10; // Position the text with 10px padding from the right edge
      doc.setTextColor(20, 20, 20); // Black text color
      doc.text(purchaseOrderText, rightX, sectionY + 35); // Align the text at the top-right

      // Draw a horizontal line to separate sections
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(5, currentY, pageWidth - 5, currentY);
      currentY += 5;

      const sectionWidth = (pageWidth - 10) / 2; // Subtracting the left and right margins
      let leftTableHeight = currentY;
      let rightTableHeight = currentY;

      // Capture the current Y position before the tables are drawn to draw the vertical line later
      const tableStartY = currentY;

      // Left table (details)
      const leftBody: any = [];
      if (Details.quoteNumber) {
        leftBody.push(["PR #", breakLongText(Details.quoteNumber || " ")]);
      }
      if (Details.quoteDate) {
        leftBody.push(["Indent Date", Details.quoteDate || " "]);
      }
      if (Details.terms) {
        leftBody.push(["Payment Terms:", Details.terms || " "]);
      }
      if (Details.creditDays) {
        leftBody.push(["Credit Days", Details.creditDays?.toString() || " "]);
      }
      doc.autoTable({
        startY: currentY,
        theme: "plain",
        showHead: "never",
        body: leftBody,
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        margin: { left: 10 },
        didDrawCell: (data: any) => {
          leftTableHeight = Math.max(
            leftTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the left table
        },
      });

      // Right table (details)
      const rightBody: any = [];
      if (Details?.placeofSupply) {
        rightBody.push(["Place of Supply", Details?.placeofSupply || " "]);
      }
      if (Details.expectedShipmentDate) {
        rightBody.push([
          "Expected Shipment Date:",
          Details.expectedShipmentDate || " ",
        ]);
      }
      if (Details?.deliveryAddressString) {
        rightBody.push([
          "Delivery Address",
          Details?.deliveryAddressString || " ",
        ]);
      }
      doc.autoTable({
        startY: currentY,
        margin: { left: sectionWidth + 10 },
        theme: "plain",
        showHead: "never",
        body: rightBody,
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the right table
        },
      });

      const maxHeight = Math.max(leftTableHeight, rightTableHeight); // Calculate the maximum height

      // Draw the vertical line separator between the left and right tables without any gap on top and bottom
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(sectionWidth + 5, tableStartY, sectionWidth + 5, maxHeight + 5); // Ensure the line extends beyond the bottom by 5 units

      // Add Supplier Details section after the tables
      currentY = maxHeight + 10; // Move below the tables
      doc.setFillColor(200, 200, 200);
      doc.rect(5, currentY, pageWidth - 10, 6, "F"); // Title background
      return currentY; // Return the final Y position to calculate the next section start point
    };

    const addTable = (doc: any, Details: any, currentY: any) => {
      const mainTitles: any = [
        { content: "Sno", rowSpan: 2 },
        { content: "Material & Description", rowSpan: 2 },
        { content: "Specification", rowSpan: 2 },
        { content: "QTY", rowSpan: 2 },
      ];

      const subTitles: any = [];
      const head = [mainTitles, subTitles];

      const rows = Details?.lines?.map((line: any, index: any) => {
        const row = [
          (index + 1).toString(),
          line.itemDescription,
          line.specification,
          line.qty || "",
        ];
        row?.push(line?.taxableAmount);
        return row;
      });

      let footer: any = [
        ["", "", "", "", "", "", "", "", { content: "" }],
        ["", "", "", "", "", "", "", "", { content: "" }],
        ["", "", "", "", "", "", "", "", { content: "" }],
        ["", "", "", "", "", "", "", "", { content: "" }],
        ["", "", "", "", "", "", "", "", { content: "" }],
        [
          // "",
          // "",
          // "",
          // "",
          // "",
          // "",
          "",
          "",
          {
            content: "Authorised Signature         ",
            colSpan: 3,
            halign: "right",
          },
          // "",
        ],
        [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          { content: "", styles: { halign: "middle" } },
        ],
      ];

      doc.autoTable({
        head,
        body: rows,
        foot: footer,
        startY: currentY,
        pageBreak: "auto",

        didParseCell: (data: any) => {
          // Align the text of the last column ('Taxable Amount', 'CGST (5%)', 'SGST (5%)', 'Total')

          if (data.section === "foot") {
            data.cell.styles.halign = "right"; // Manually set right alignment
          }
        },
        didDrawCell: (data: any) => {
          doc.setDrawColor(150, 150, 150);
        },
        headStyles: {
          fontSize: 9,
          fillColor: [227, 227, 227],
          textColor: [31, 31, 31],
          valign: "middle",
          cellPadding: 2.5,
          lineWidth: 0.5,
          lineHeight: 1.5,
        },
        footStyles: {
          fontSize: 9,
          textColor: [31, 31, 31],
          lineWidth: 0,
        },
        styles: {
          fillColor: [255, 255, 255],
          fontSize: 8,
          lineWidth: 0.2,
          cellPadding: 1.5,
          overflow: "linebreak",
        },

        theme: "grid",
        margin: { top: 20, bottom: 20, left: 5.1, right: 5.1 },
        showFoot: "lastPage",
      });

      // Update page number for all pages
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageText = `Page ${i} of ${pageCount}`;
        doc.text(pageText, pageWidth - 20, pageHeight - 10, { align: "right" });
      }
    };

    const addApprovalsSection = async (doc: jsPDF, approvals: any[]) => {
      if (!approvals || approvals.length === 0) {
        return;
      }

      // Add a new page for approvals
      doc.addPage();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Match the spacing from company name section
      // Border box starts at Y=20, company name starts at Y=26 (sectionY + 10 where sectionY=16)
      // So spacing from border to text is 6mm - apply same spacing for Approvals heading
      const borderY = 20; // Border box Y position (from drawBorderedBox)
      let currentY = borderY + 6; // Same spacing as company name (6mm from border)

      // Add title
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Approvals", pageWidth / 2, currentY, { align: "center" });

      // Add spacing between heading and table
      currentY += 8; // Reduced spacing between heading and table

      // Format date and time helper
      const formatDateTime = (dateString: string | null) => {
        if (!dateString) return "";
        try {
          // Handle date-only strings (e.g., "2025-12-18") by parsing components directly
          let date: Date;
          if (
            typeof dateString === "string" &&
            !dateString.includes("T") &&
            !dateString.includes(" ")
          ) {
            // Date-only string (YYYY-MM-DD), parse components to avoid timezone issues
            const parts = dateString.split("-");
            if (parts.length === 3) {
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
              const day = parseInt(parts[2], 10);
              date = new Date(year, month, day, 12, 0, 0); // Use local time noon
            } else {
              date = new Date(dateString);
            }
          } else {
            date = new Date(dateString);
          }

          // Check if date is valid
          if (isNaN(date.getTime())) return "";

          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          // If dateString doesn't include time, only return date
          if (
            typeof dateString === "string" &&
            !dateString.includes("T") &&
            !dateString.includes(" ")
          ) {
            return formattedDate;
          }

          const formattedTime = date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          return `${formattedDate} ${formattedTime}`;
        } catch (error) {
          return "";
        }
      };

      // Prepare approval rows with signature data
      const approvalRowsData = await Promise.all(
        approvals.map(async (approval: any) => {
          let statusText = "";
          const status = (approval.status || "").toUpperCase();
          if (status === "PENDING") {
            const approvalDateTime = formatDateTime(approval.approvalDate);
            statusText = approvalDateTime
              ? `Pending\nDate: ${approvalDateTime}`
              : "Pending";
          } else if (status === "APPROVED") {
            const approvedDateTime = formatDateTime(approval.approvalDate);
            statusText = approvedDateTime
              ? `Approved\nDate: ${approvedDateTime}`
              : "Approved";
          } else if (status === "REJECTED") {
            const rejectedDateTime = formatDateTime(approval.approvalDate);
            statusText = rejectedDateTime
              ? `Rejected\nDate: ${rejectedDateTime}`
              : "Rejected";
          } else {
            statusText = approval.status || "";
          }

          // Fetch signature image if available
          // dms can be an array or object, handle both cases
          let signatureImage: string | null = null;
          let signaturePath = null;

          if (Array.isArray(approval.dms) && approval.dms.length > 0) {
            // If dms is an array, get the first element's filePath
            signaturePath = approval.dms[0]?.filePath;
          } else if (approval.dms?.filePath) {
            // If dms is an object with filePath
            signaturePath = approval.dms.filePath;
          }

          if (signaturePath) {
            try {
              signatureImage = await fetchImageAsBase64(signaturePath);
            } catch (error) {
              console.error("Error fetching signature image:", error);
              signatureImage = null;
            }
          }

          return {
            name: approval.userName || "",
            role: approval.roleName || "",
            level: approval.level || approval.levelName || "",
            status: statusText,
            signature: signatureImage,
          };
        })
      );

      // Prepare table body (signature column will be handled in didDrawCell)
      const approvalRows = approvalRowsData.map((row: any) => [
        row.name,
        row.role,
        row.level,
        row.status,
        row.signature ? "" : "", // Empty string instead of "N/A"
      ]);

      // Calculate proper column widths based on page width
      const availableWidth = pageWidth - 20; // 10mm margin on each side

      // Add approvals table with signature column
      doc.autoTable({
        head: [["Name", "Role", "Level", "Status", "Signature"]],
        body: approvalRows,
        startY: currentY,
        margin: { top: 0, left: 10, right: 10, bottom: 10 },
        theme: "grid",
        headStyles: {
          fontSize: 10,
          fillColor: [227, 227, 227],
          textColor: [31, 31, 31],
          valign: "middle",
          halign: "center",
          cellPadding: 4,
          lineWidth: 0.5,
        },
        styles: {
          fontSize: 9,
          lineWidth: 0.2,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
        },
        columnStyles: {
          0: {
            cellWidth: availableWidth * 0.2,
            halign: "left",
            valign: "middle",
          },
          1: {
            cellWidth: availableWidth * 0.2,
            halign: "left",
            valign: "middle",
          },
          2: {
            cellWidth: availableWidth * 0.12,
            halign: "left",
            valign: "middle",
          },
          3: {
            cellWidth: availableWidth * 0.28,
            halign: "left",
            valign: "middle",
          },
          4: {
            cellWidth: availableWidth * 0.2,
            halign: "center",
            valign: "middle",
          },
        },
        didDrawCell: (data: any) => {
          // Draw signature images in the signature column (column index 4)
          if (data.column.index === 4 && data.section === "body") {
            const rowIndex = data.row.index;
            const signatureData = approvalRowsData[rowIndex];
            if (signatureData?.signature) {
              try {
                // Calculate image position within the cell (centered with proper padding)
                const cellPadding = 4;
                const imgWidth = Math.min(
                  35,
                  data.cell.width - cellPadding * 2
                );
                const imgHeight = Math.min(
                  15,
                  data.cell.height - cellPadding * 2
                );

                // Center the image within the cell, ensuring it stays within cell boundaries
                const x = data.cell.x + (data.cell.width - imgWidth) / 2;
                const y =
                  data.cell.y +
                  cellPadding +
                  (data.cell.height - cellPadding * 2 - imgHeight) / 2;

                doc.addImage(
                  signatureData.signature,
                  "PNG",
                  x,
                  y,
                  imgWidth,
                  imgHeight
                );
              } catch (error) {
                console.error("Error adding signature image:", error);
              }
            }
          }
        },
      });

      // Ensure the approvals page is properly set as the current page
      const totalPages = (doc as any).internal.getNumberOfPages();
      doc.setPage(totalPages); // Set to the approvals page (last page)

      // Update page numbers after adding approvals page
      doc.setFontSize(8);
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const pageText = `Page ${i} of ${totalPages}`;
        doc.text(pageText, pageWidth - 20, pageHeight - 10, { align: "right" });
      }

      // Return to the approvals page to ensure it's finalized
      doc.setPage(totalPages);
    };

    // Generate PDF
    // const leftLogo = await fetchImageAsBase64(
    //   `https://procurexdevuatstg.blob.core.windows.net/procurexuatblob/prc8.png`
    // );
    // const leftLogo = await fetchImageAsBase64(buyerOrgLogo);

    let leftLogo: string | null = null;

    if (buyerOrgLogo) {
      try {
        leftLogo = await fetchImageAsBase64(buyerOrgLogo);
      } catch (error) {
        console.error("Buyer logo load failed", error);
        leftLogo = null;
      }
    }
    const currentY = await addGeneralDetailsSection(
      doc,
      buyerDetails,
      Details,
      supplierDetails
    );

    addTable(doc, Details, currentY);

    // Add approvals section if available
    if (prApprovals && prApprovals.length > 0) {
      await addApprovalsSection(doc, prApprovals);
    }

    const applyHeaderAndFooter = async () => {
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await addHeader(doc, leftLogo);
        await addFooter(doc);
        drawBorderedBox(doc); // Ensure borders are added to each page if needed
      }
    };
    await applyHeaderAndFooter();

    // Ensure all pages are properly finalized before saving/downloading
    const finalPageCount = (doc as any).internal.getNumberOfPages();

    // Make sure we're on the first page before saving to ensure all pages are included
    if (finalPageCount > 0) {
      doc.setPage(1);
    }

    if (download) {
      // Use output('blob') then save to ensure all pages are included
      const pdfBlob = doc.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = (Details?.quoteNumber || "PR") + ".pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    // Generate the PDF as a Blob
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setPdfUrl(pdfUrl); // Set the Blob URL to display the PDF inline
  };
  const downloadPDF = async () => {
    await generatePDF(true);
  };
  return (
    <>
      <Button type="button" color="" onClick={() => generatePDF()}>
        <Icon name="file-pdf" /> PDF
      </Button>
      {pdfUrl && (
        <Modal
          open={pdfUrl !== null}
          onCancel={() => setPdfUrl(null)}
          width={"90vw"}
          footer={null}
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Purchase Request </span>
              <span
                style={{ cursor: "pointer" }}
                onClick={async () => {
                  await downloadPDF();
                }}
              >
                Download
                <Icon name="download" className="me-4" />
              </span>
            </div>
          }
        >
          <iframe
            src={pdfUrl + "#toolbar=0"}
            style={{
              width: "100%",
              height: "80vh",
            }}
            title="PDF Viewer"
          ></iframe>
        </Modal>
      )}
    </>
  );
};

export default PdfGenerator;
